import { useState } from "react";
import Cropper from "react-easy-crop";
import type { Area, MediaSize } from "react-easy-crop";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Spinner";

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function getCroppedImg(src: string, crop: Area, original: File): Promise<File> {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
  return new Promise((res) =>
    canvas.toBlob(
      (b) => res(new File([b!], original.name, { type: "image/jpeg" })),
      "image/jpeg",
      0.95
    )
  );
}

type ImageCropDialogProps = {
  open: boolean;
  imageUrl: string;
  aspect: number;
  originalFile: File;
  onApply: (file: File) => void;
  onCancel: () => void;
};

export function ImageCropDialog({
  open,
  imageUrl,
  aspect,
  originalFile,
  onApply,
  onCancel,
}: ImageCropDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [applying, setApplying] = useState(false);
  // zoom=1 is "cover" (fills the frame) — minZoom must reach "contain" (full image) per-image, not a fixed floor.
  const [minZoom, setMinZoom] = useState(0.3);

  function handleMediaLoaded(mediaSize: MediaSize) {
    const imageAspect = mediaSize.naturalWidth / mediaSize.naturalHeight;
    setMinZoom(Math.min(aspect / imageAspect, imageAspect / aspect));
  }

  async function handleApply() {
    if (!croppedAreaPixels) return;
    setApplying(true);
    try {
      const file = await getCroppedImg(imageUrl, croppedAreaPixels, originalFile);
      onApply(file);
    } finally {
      setApplying(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-lg" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Crop image</DialogTitle>
        </DialogHeader>
        <div className="relative w-full h-72 bg-black rounded-lg overflow-hidden">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            minZoom={minZoom}
            aspect={aspect}
            restrictPosition={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
            onMediaLoaded={handleMediaLoaded}
          />
        </div>
        <div className="flex items-center gap-3 px-1">
          <span className="text-xs text-muted-foreground shrink-0">Zoom</span>
          <input
            type="range"
            min={minZoom}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full accent-primary"
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={applying}>
            Cancel
          </Button>
          <Button type="button" onClick={handleApply} disabled={applying || !croppedAreaPixels}>
            {applying ? <Spinner className="size-4 border-2" /> : "Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
